import { ReactElement } from "react";
import { ConfigProvider, Select } from "antd";
import { data as currencyCodes } from "currency-codes";
import CurrencyFlag from "react-currency-flags";
import "./CurrencyPicker.css";

/**
 * App-wide default currency picker.
 *
 * Wraps antd `Select` with the full list of ISO currencies (flag + code +
 * name), searchable by code or name, themed to the app's primary orange
 * (`#FF6929`, matching DatePicker/DateRangePicker) with a thin black dropdown
 * scrollbar.
 *
 * Use `variant="prefix"` for the compact selector that sits inside a price
 * input (replaces the old `#E9E7E5` trigger button).
 */
export interface CurrencyPickerProps {
  /** Selected ISO currency code, e.g. "USD". */
  value?: string;
  /** Called with the newly selected currency code. */
  onChange: (code: string) => void;
  placeholder?: string;
  /** Extra classes applied to the antd Select. */
  className?: string;
  disabled?: boolean;
  /**
   * "default" — full-width field (h-48px).
   * "prefix" — compact borderless selector for sitting inside a price input.
   */
  variant?: "default" | "prefix";
  /** Popup (dropdown) width in px. */
  popupWidth?: number;
  /** Height class for the default variant (ignored by the prefix variant). */
  heightClass?: string;
  /**
   * When true, the collapsed/selected state shows only the flag + code
   * (used by the prefix variant automatically).
   */
  compactValue?: boolean;
}

// Built once at module scope — the currency list is static.
const CURRENCY_OPTIONS = currencyCodes.map((item) => ({
  value: item.code,
  search: `${item.code} ${item.currency}`,
  label: (
    <div className="flex items-center gap-2">
      <CurrencyFlag currency={item.code} size="sm" />
      <span className="font-medium">{item.code}</span>
      <span className="text-gray-500">{item.currency}</span>
    </div>
  ),
  shortLabel: (
    <div className="flex items-center gap-1.5">
      <CurrencyFlag currency={item.code} size="sm" />
      <span className="font-medium">{item.code}</span>
    </div>
  ),
}));

export default function CurrencyPicker({
  value,
  onChange,
  placeholder = "Choose Currency",
  className = "",
  disabled,
  variant = "default",
  popupWidth = 320,
  compactValue = false,
  heightClass = "h-[48px]",
}: CurrencyPickerProps): ReactElement {
  const isPrefix = variant === "prefix";

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#FF6929",
          colorInfo: "#FF6929",
          borderRadius: 8,
        },
      }}
    >
      <Select
        showSearch
        disabled={disabled}
        variant={isPrefix ? "borderless" : undefined}
        className={`currency-picker no-focus-select outline-none ${
          isPrefix ? "currency-picker--prefix" : `w-full ${heightClass}`
        } ${className}`
          .replace(/\s+/g, " ")
          .trim()}
        popupClassName="currency-picker-popup"
        popupMatchSelectWidth={popupWidth}
        placeholder={placeholder}
        options={CURRENCY_OPTIONS}
        value={value || undefined}
        optionFilterProp="search"
        optionLabelProp={isPrefix || compactValue ? "shortLabel" : "label"}
        filterOption={(input, option) =>
          (option?.search as string)
            ?.toLowerCase()
            .includes(input.toLowerCase())
        }
        onChange={(code) => onChange(code)}
      />
    </ConfigProvider>
  );
}
